import { useEffect } from "react";
import { SITE_NAME, SITE_DESC, SITE_KEYWORDS, BASE_URL } from "@/config/site";

interface SeoOptions {
  title: string;
  description?: string;
  keywords?: string;
  path?: string;
  jsonLd?: object | object[];
}

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function syncJsonLd(jsonLd?: object | object[]) {
  document.querySelectorAll("script[data-readdy-jsonld]").forEach((n) => n.remove());
  if (!jsonLd) return;
  const scripts = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
  scripts.forEach((schema) => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute("data-readdy-jsonld", "true");
    s.textContent = JSON.stringify(schema);
    document.head.appendChild(s);
  });
}

export default function useSeo({
  title,
  description = SITE_DESC,
  keywords = SITE_KEYWORDS,
  path = "/",
  jsonLd,
}: SeoOptions) {
  useEffect(() => {
    const fullTitle = `${title} - ${SITE_NAME}`;
    document.title = fullTitle;

    upsertMeta("name", "description", description);
    upsertMeta("name", "keywords", keywords);
    upsertMeta("name", "robots", "index, follow");
    upsertMeta("name", "author", SITE_NAME);

    const canonical = `${BASE_URL}${path}`;
    upsertLink("canonical", canonical);

    // Open Graph
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:locale", "zh_CN");

    // Twitter Card
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:url", canonical);

    // last-modified
    const lm = document.head.querySelector<HTMLMetaElement>('meta[http-equiv="last-modified"]');
    if (lm) lm.setAttribute("content", new Date().toUTCString());

    syncJsonLd(jsonLd);
  }, [title, description, keywords, path, jsonLd]);
}